import React, { useState } from 'react';
import { Box, Typography, Chip, Tooltip, Collapse, alpha } from '@mui/material';
import {
  ExpandMore, ExpandLess, FiberManualRecord,
  TrendingUp, AutoAwesome, Assessment, ShowChart,
  Timeline, Bolt, Diamond, Balance, SwapHoriz, Inventory, BarChart,
  RocketLaunch, CandlestickChart,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMarketStageLabel } from '../../utils/marketStageLabels';
import { getScoreColor } from '../../theme';

const STYLE_LABEL_KEYS = {
  FLOW_LEADERS: 'vnstock.recommendations.styles.flowLeaders',
  QUALITY_GROWTH: 'vnstock.recommendations.styles.qualityGrowth',
  VALUE_WITH_CONFIRMATION: 'vnstock.recommendations.styles.valueConfirmation',
  TURNAROUND: 'vnstock.recommendations.styles.turnaround',
  ACCUMULATION_BREAKOUT: 'vnstock.recommendations.styles.accumulationBreakout',
  REVERSAL: 'vnstock.recommendations.styles.reversal',
  SUPER_STOCK: 'vnstock.recommendations.styles.superStock',
  PRICE_ACTION: 'vnstock.recommendations.styles.priceAction',
};

// ── Style maps ────────────────────────────────────
const STYLE_MAP = {
  FLOW_LEADERS:                  { short: { vi: 'Dòng tiền', en: 'Flow' }, color: '#667eea', Icon: TrendingUp },
  QUALITY_GROWTH:                { short: { vi: 'Chất lượng', en: 'Quality' }, color: '#10b981', Icon: AutoAwesome },
  VALUE_WITH_CATALYST:           { short: 'Val.Cat.',   color: '#f59e0b', Icon: ShowChart },
  VALUE_WITH_CONFIRMATION:       { short: { vi: 'Định giá', en: 'Val.Conf.' }, color: '#8b5cf6', Icon: Assessment },
  MOMENTUM_BREAKOUT:             { short: 'Momentum',   color: '#ec4899', Icon: Bolt },
  GROWTH_AT_REASONABLE_PRICE:    { short: 'GARP',       color: '#06b6d4', Icon: Balance },
  DEEP_VALUE:                    { short: 'Deep Val.',  color: '#14b8a6', Icon: Diamond },
  TURNAROUND:                    { short: { vi: 'Hồi phục', en: 'Turnaround' }, color: '#f97316', Icon: SwapHoriz },
  ACCUMULATION:                  { short: 'Accum.',     color: '#6366f1', Icon: Inventory },
  ACCUMULATION_BREAKOUT:         { short: { vi: 'TL-BP', en: 'Accum.BO' }, color: '#7c3aed', Icon: Timeline },
  REVERSAL:                      { short: { vi: 'Đảo chiều', en: 'Reversal' }, color: '#e11d48', Icon: SwapHoriz },
  SUPER_STOCK:                   { short: { vi: 'Siêu CP', en: 'Super' }, color: '#06b6d4', Icon: RocketLaunch },
  PRICE_ACTION:                  { short: { vi: 'Giá', en: 'PA' }, color: '#e97320', Icon: CandlestickChart },
};

const getDefault = (style) => ({ short: style.replace(/_/g, ' ').substring(0, 10), color: '#64748b', Icon: BarChart });

// ── Helpers ────────────────────────────────────
const getSectorName = (sector, lang = 'vi') => {
  if (!sector) return '—';
  if (typeof sector === 'string') return sector;
  return lang === 'vi' ? (sector.vi || sector.en || '—') : (sector.en || sector.vi || '—');
};

const formatMCap = (v) => {
  if (v == null) return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  if (isNaN(n)) return v;
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)}M`;
  return n.toLocaleString();
};

const getLocalizedCopy = (value, lang = 'vi') => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value[lang] || value.vi || value.en || '';
  return '';
};

const getVisibleMarketStageOverlay = (overlay, lang = 'vi') => {
  const text = getLocalizedCopy(overlay?.explanation, lang);
  const pct = overlay?.adjustment_pct;
  const visible = typeof pct === 'number' && Math.abs(pct) >= 2.5 && Boolean(text);
  return {
    visible,
    text,
    pct,
    positive: typeof pct === 'number' ? pct >= 0 : true,
  };
};

const getLocalizedStyleLabel = (style, t) => t(STYLE_LABEL_KEYS[style] || style, style.replace(/_/g, ' '));


// ── Component ────────────────────────────────────
const HighConvictionStocksCard = ({ highConvictionStocks = [], marketStageContext = null }) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'vi';

  if (highConvictionStocks.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Toggle header */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', mb: 0.5,
          '&:hover': { opacity: 0.8 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecord sx={{ fontSize: 8, color: '#8b5cf6' }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>
            {t('vnstock.recommendations.highConviction.title', 'High Conviction Picks')}
          </Typography>
          <Chip
            label={t('vnstock.recommendations.highConviction.summary', {
              count: highConvictionStocks.length,
              defaultValue: `${highConvictionStocks.length} stocks · appears in at least 2 styles`,
            })}
            size="small"
            sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, bgcolor: '#f5f3ff', color: '#7c3aed', border: '1px solid #e9d5ff' }}
          />
        </Box>
        {open ? <ExpandLess sx={{ fontSize: 18, color: '#94a3b8' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#94a3b8' }} />}
      </Box>

      <Collapse in={open}>
        <Box sx={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {marketStageContext && (
            <Box sx={{
              px: 1.5,
              py: 0.95,
              borderBottom: '1px solid #ede9fe',
              background: 'linear-gradient(180deg, #fcfaff 0%, #faf5ff 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9 }}>
                <FiberManualRecord sx={{ fontSize: 9, color: '#7c3aed', mt: '6px', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.35px', color: '#7c3aed' }}>
                      {t('vnstock.recommendations.highConviction.marketStageTitle', 'Current market phase')}
                    </Typography>
                    {(marketStageContext?.stages || []).slice(0, 2).map((stage) => (
                      <Chip
                        key={stage}
                        label={getMarketStageLabel(t, stage)}
                        size="small"
                        sx={{ height: 18, fontSize: '0.56rem', fontWeight: 700, bgcolor: '#f5f3ff', color: '#7c3aed', border: '1px solid #e9d5ff' }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Header row */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '32px 1.5fr 1.2fr 2.5fr 65px 55px',
            gap: '0 8px',
            p: '6px 12px',
            background: 'linear-gradient(180deg, #faf5ff 0%, #f5f3ff 100%)',
            borderBottom: '1px solid #e9d5ff'
          }}>
            {[
              { label: '#', align: 'left' },
              { label: t('vnstock.recommendations.highConviction.columns.ticker', 'Ticker'), align: 'left' },
              { label: t('vnstock.recommendations.highConviction.columns.sectorMarketCap', 'Sector / Market cap'), align: 'left' },
              { label: t('vnstock.recommendations.highConviction.columns.styles', 'Styles'), align: 'left' },
              { label: t('vnstock.recommendations.highConviction.columns.avgScore', 'Avg score'), align: 'center' },
              { label: t('vnstock.recommendations.highConviction.columns.match', 'Match'), align: 'center' },
            ].map((h, i) => (
              <Typography key={i} sx={{
                fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.3px', color: '#7c3aed', textAlign: h.align
              }}>
                {h.label}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {highConvictionStocks.map((stock, idx) => {
            const scoreColor = getScoreColor(stock.avgScoreLevel);
            const strongestOverlay = (stock.marketStageOverlays || [])
              .slice()
              .sort((a, b) => Math.abs(b?.adjustment_pct || 0) - Math.abs(a?.adjustment_pct || 0))[0];
            const overlay = getVisibleMarketStageOverlay(strongestOverlay, lang);

            return (
              <Box
                key={stock.ticker}
                onClick={() => navigate(`/stocks/${stock.ticker}`, { state: { from: `${location.pathname}${location.search}${location.hash}` } })}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '32px 1.5fr 1.2fr 2.5fr 65px 55px',
                  gap: '0 8px',
                  p: '7px 12px',
                  alignItems: 'center',
                  borderBottom: '1px solid #f8fafc',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  '&:hover': { background: '#faf5ff' },
                  ...(idx % 2 === 1 ? { bgcolor: '#fafbfc' } : {})
                }}
              >
                {/* Rank */}
                <Box sx={{
                  width: 22, height: 22, borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: idx === 0 ? 'linear-gradient(135deg, #667eea, #764ba2)'
                    : idx === 1 ? 'linear-gradient(135deg, #f093fb, #f5576c)'
                    : idx === 2 ? 'linear-gradient(135deg, #4facfe, #00f2fe)'
                    : '#e2e8f0',
                  color: idx < 3 ? '#fff' : '#64748b',
                  fontSize: '0.68rem', fontWeight: 800
                }}>
                  {idx + 1}
                </Box>

                {/* Ticker + Company */}
                <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                    {stock.ticker}
                  </Typography>
                  {stock.companyName && (
                    <Tooltip title={stock.companyName} arrow placement="right">
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                        {stock.companyName}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>

                {/* Sector + Market Cap */}
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                    {getSectorName(stock.sector, lang)}
                  </Typography>
                  {stock.marketCap && (
                    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {formatMCap(stock.marketCap)}
                    </Typography>
                  )}
                </Box>

                {/* Style chips */}
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                    {stock.styles.map(style => {
                      const cfg = STYLE_MAP[style] || getDefault(style);
                      const IconComp = cfg.Icon;
                      const shortLabel = getLocalizedCopy(cfg.short, lang) || cfg.short;
                      return (
                        <Tooltip key={style} title={getLocalizedStyleLabel(style, t)} arrow placement="top">
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.3,
                            px: 0.6, py: 0.15, borderRadius: 0.75,
                            bgcolor: alpha(cfg.color, 0.08), color: cfg.color,
                            border: `1px solid ${alpha(cfg.color, 0.15)}`,
                          }}>
                            <IconComp sx={{ fontSize: 10 }} />
                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, lineHeight: 1.2 }}>
                              {shortLabel}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                  {overlay.visible && (
                    <Tooltip title={overlay.text} arrow placement="top">
                      <Box sx={{ mt: 0.45 }}>
                        <Chip
                          size="small"
                          label={`${t('vnstock.recommendations.highConviction.marketStageBoost', 'Market phase')} ${overlay.pct > 0 ? '+' : ''}${overlay.pct.toFixed(1)}%`}
                          sx={{
                            height: 18,
                            maxWidth: '100%',
                            fontSize: '0.56rem',
                            fontWeight: 700,
                            bgcolor: overlay.positive ? alpha('#7c3aed', 0.08) : '#fef2f2',
                            color: overlay.positive ? '#7c3aed' : '#b91c1c',
                            border: `1px solid ${overlay.positive ? alpha('#7c3aed', 0.14) : '#fecaca'}`,
                            '& .MuiChip-label': {
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* Average Score */}
                <Box sx={{ textAlign: 'center' }}>
                  {stock.avgScore != null ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: scoreColor, fontFamily: 'monospace', lineHeight: 1 }}>
                        {stock.avgScore.toFixed(1)}
                      </Typography>
                      <Box sx={{ width: 32, height: 3, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${stock.avgScore}%`, bgcolor: scoreColor, borderRadius: 2 }} />
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>
                  )}
                </Box>

                {/* Match count */}
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.3,
                    px: 0.8, py: 0.2, borderRadius: 1,
                    bgcolor: stock.count >= 4 ? '#667eea' : stock.count >= 3 ? '#8b5cf6' : '#a78bfa',
                    color: '#fff',
                  }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, lineHeight: 1 }}>
                      {stock.count}
                    </Typography>
                    <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, opacity: 0.85, lineHeight: 1 }}>
                      {t('vnstock.recommendations.highConviction.styles', 'styles')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default HighConvictionStocksCard;
